import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, Mail, Lock, User, AlertCircle, ArrowRight, Eye, EyeOff, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Signup = () => {
  const [formData, setFormData] = useState({
    username: '', email: '', password: '', confirmPassword: '', firstName: '', lastName: ''
  });
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const passwordStrength = (() => {
    const p = formData.password;
    if (!p) return { level: 0, label: '', color: '' };
    let score = 0;
    if (p.length >= 6) score++;
    if (p.length >= 10) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    if (score <= 1) return { level: 1, label: 'Weak', color: '#EF4444' };
    if (score <= 3) return { level: 2, label: 'Fair', color: '#F59E0B' };
    return { level: 3, label: 'Strong', color: '#10B981' };
  })();

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!acceptedPrivacy) { setError('You must accept the Privacy Policy'); return; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/users/register`, {
        username: formData.username, email: formData.email, password: formData.password,
        first_name: formData.firstName, last_name: formData.lastName
      });
      localStorage.setItem('user_token', response.data.access_token);
      localStorage.setItem('user_data', JSON.stringify(response.data.user));
      navigate('/');
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] flex items-center justify-center px-6 py-20" data-testid="signup-page">
      {/* Ambient */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 right-1/3 w-[500px] h-[500px] bg-[#8A2BE2]/[0.04] rounded-full blur-[150px]" />
        <div className="absolute bottom-1/3 left-1/3 w-[400px] h-[400px] bg-[#00E5FF]/[0.04] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 w-full max-w-sm" style={{ animation: 'fadeInUp 0.5s ease-out' }}>
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-gradient-to-br from-[#8A2BE2]/20 to-[#00E5FF]/10 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-white/[0.06]">
            <UserPlus className="w-6 h-6 text-[#8A2BE2]" />
          </div>
          <h1 className="text-2xl font-light tracking-tight text-white mb-1">Create Account</h1>
          <p className="text-white/30 text-sm">Join Starnight Holdings</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSignup} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2.5 p-3.5 rounded-xl bg-red-500/5 border border-red-500/15" data-testid="signup-error">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span className="text-red-400 text-xs">{error}</span>
            </div>
          )}

          {/* Name row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] text-white/30 mb-2 uppercase tracking-wider font-medium">First Name</label>
              <input type="text" value={formData.firstName} onChange={(e) => update('firstName', e.target.value)} required
                data-testid="signup-firstname-input" placeholder="First"
                className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-[#00E5FF]/40 focus:border-[#00E5FF]/30 transition-all text-sm" />
            </div>
            <div>
              <label className="block text-[11px] text-white/30 mb-2 uppercase tracking-wider font-medium">Last Name</label>
              <input type="text" value={formData.lastName} onChange={(e) => update('lastName', e.target.value)} required
                data-testid="signup-lastname-input" placeholder="Last"
                className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-[#00E5FF]/40 focus:border-[#00E5FF]/30 transition-all text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] text-white/30 mb-2 uppercase tracking-wider font-medium">Username</label>
            <div className="relative">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <input type="text" value={formData.username} onChange={(e) => update('username', e.target.value)} required
                data-testid="signup-username-input" placeholder="Choose a username"
                className="w-full pl-10 pr-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-[#00E5FF]/40 focus:border-[#00E5FF]/30 transition-all text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] text-white/30 mb-2 uppercase tracking-wider font-medium">Email</label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <input type="email" value={formData.email} onChange={(e) => update('email', e.target.value)} required
                data-testid="signup-email-input" placeholder="you@example.com"
                className="w-full pl-10 pr-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-[#00E5FF]/40 focus:border-[#00E5FF]/30 transition-all text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] text-white/30 mb-2 uppercase tracking-wider font-medium">Password</label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <input type={showPassword ? 'text' : 'password'} value={formData.password}
                onChange={(e) => update('password', e.target.value)} required
                data-testid="signup-password-input" placeholder="Min. 6 characters"
                className="w-full pl-10 pr-11 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-[#00E5FF]/40 focus:border-[#00E5FF]/30 transition-all text-sm" />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/50 transition-colors">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {formData.password && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 flex gap-1">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-1 flex-1 rounded-full transition-all" style={{
                      backgroundColor: i <= passwordStrength.level ? passwordStrength.color : 'rgba(255,255,255,0.05)'
                    }} />
                  ))}
                </div>
                <span className="text-[10px]" style={{ color: passwordStrength.color }}>{passwordStrength.label}</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-[11px] text-white/30 mb-2 uppercase tracking-wider font-medium">Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <input type={showPassword ? 'text' : 'password'} value={formData.confirmPassword}
                onChange={(e) => update('confirmPassword', e.target.value)} required
                data-testid="signup-confirm-password-input" placeholder="Confirm password"
                className={`w-full pl-10 pr-11 py-3 bg-white/[0.03] border rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 transition-all text-sm ${
                  formData.confirmPassword && formData.confirmPassword !== formData.password
                    ? 'border-red-500/30 focus:ring-red-500/30'
                    : formData.confirmPassword && formData.confirmPassword === formData.password
                    ? 'border-emerald-500/30 focus:ring-emerald-500/30'
                    : 'border-white/[0.08] focus:ring-[#00E5FF]/40 focus:border-[#00E5FF]/30'
                }`} />
              {formData.confirmPassword && formData.confirmPassword === formData.password && (
                <CheckCircle className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" />
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex items-center gap-2.5 p-3 rounded-xl bg-[#00E5FF]/[0.03] border border-[#00E5FF]/10">
            <CheckCircle className="w-4 h-4 text-[#00E5FF] flex-shrink-0" />
            <span className="text-white/30 text-xs">You'll receive newsletters and exclusive updates</span>
          </div>

          {/* Privacy */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative mt-0.5">
              <input type="checkbox" checked={acceptedPrivacy} onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                data-testid="privacy-checkbox"
                className="sr-only peer" />
              <div className="w-4 h-4 rounded border border-white/20 bg-white/[0.03] peer-checked:bg-[#00E5FF] peer-checked:border-[#00E5FF] transition-all flex items-center justify-center">
                {acceptedPrivacy && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-white/25 text-xs leading-relaxed">
              I agree to the{' '}
              <Link to="/privacy" target="_blank" className="text-[#00E5FF] hover:text-[#00E5FF]/80 underline transition-colors">
                Privacy Policy
              </Link>
              <span className="text-red-400 ml-0.5">*</span>
            </span>
          </label>

          <Button type="submit" disabled={loading} data-testid="signup-submit-btn"
            className="w-full bg-white text-black hover:bg-white/90 font-medium py-3 rounded-xl text-sm transition-all disabled:opacity-30">
            {loading ? 'Creating account...' : 'Create Account'}
            {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </form>

        {/* Links */}
        <div className="mt-8 text-center space-y-3">
          <p className="text-white/20 text-xs">
            Already have an account?{' '}
            <Link to="/login" className="text-[#00E5FF] hover:text-[#00E5FF]/80 transition-colors font-medium" data-testid="goto-login-link">
              Sign In
            </Link>
          </p>
          <Link to="/" className="block text-white/15 hover:text-white/30 text-xs transition-colors">
            Back to website
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Signup;
